<script setup lang="ts">
import CBaseBlock from '@/presentation/components/base/CBaseBlock.vue'
import { default as PMultiSelect } from 'primevue/multiselect'
import { useExportsStore } from '@/presentation/stores/exports/exports.ts'
import { storeToRefs } from 'pinia'
import { computed, type ComputedRef, type Ref, ref, watch } from 'vue'
import type ExportFilterSchool from '@/core/domain/exports/ExportFilterSchool.ts'
import type ExportFilterSchoolClassroom from '@/core/domain/exports/ExportFilterSchoolClassroom.ts'
import { ClassroomDegree } from '@/core/domain/resources/enums/ClassroomDegree.ts'
import ExportFilterInput from '@/core/domain/exports/ExportFilterInput.ts'
import { default as PButton } from 'primevue/button'
import { ExportType } from '@/core/domain/resources/enums/ExportType.ts'
import { QuizType } from '@/core/domain/resources/enums/QuizType.ts'
import { saveAs } from 'file-saver'

interface MultiSelectOption<T = string | number> {
  label: string
  value: T
}
interface GroupMultiSelectOptions<T = string | number> {
  label: string
  items: MultiSelectOption<T>[]
}

// Composables
const exportsStore = useExportsStore()

// Store refs
const { sourceFilters, exportSelectedSchoolYear, existsReportingState } = storeToRefs(exportsStore)

// Refs
const filtersInput: Ref<ExportFilterInput> = ref(ExportFilterInput.fromYear(0))

// Helpers
const schoolLabelBuilder = (s: ExportFilterSchool) => `${s.name} (${s.city}, ${s.zipCode})`
const selectedSchoolIds = computed(() => new Set<number>(filtersInput.value.schools ?? []))
const selectedDegrees = computed(
  () => new Set<ClassroomDegree.Values>(filtersInput.value.degrees ?? [])
)

const useAllSchools = computed(() => selectedSchoolIds.value.size === 0)
const useAllDegrees = computed(() => selectedDegrees.value.size === 0)

// Create a cache for school ID and label
const schoolsById = computed(() => new Map(sourceFilters.value.map((s) => [s.id, s])))
const schoolLabelCache = computed(() => {
  const m = new Map<number, string>()
  for (const s of sourceFilters.value) m.set(s.id, schoolLabelBuilder(s))
  return m
})

/**
 * List of school to export
 */
const schoolsPool = computed<ExportFilterSchool[]>(() => {
  if (useAllSchools.value) return sourceFilters.value
  const out: ExportFilterSchool[] = []
  for (const id of selectedSchoolIds.value) {
    const s = schoolsById.value.get(id)
    if (s) out.push(s)
  }
  return out
})

/**
 * List of school for the select options
 */
const schoolsOptions: ComputedRef<MultiSelectOption<number>[]> = computed(() => {
  return sourceFilters.value.map((school: ExportFilterSchool) => ({
    label: schoolLabelCache.value.get(school.id) ?? 'Inconnu',
    value: school.id
  }))
})

/**
 * List of school degrees for the select options
 */
const degreesOptions: ComputedRef<MultiSelectOption<ClassroomDegree.Values>[]> = computed(() => {
  const present = new Set<ClassroomDegree.Values>(
    schoolsPool.value.flatMap((s) => s.classrooms.map((c) => c.degree)).filter(Boolean)
  )
  return ClassroomDegree.options.filter((o) => present.has(o.value))
})

/**
 * List of school classroom for the select options
 */
const classroomsOptions: ComputedRef<GroupMultiSelectOptions<number>[]> = computed(() => {
  return schoolsPool.value.map((school) => {
    return {
      label: schoolLabelCache.value.get(school.id) ?? 'Inconnu',
      items: school.classrooms
        .filter(
          (classroom: ExportFilterSchoolClassroom) =>
            useAllDegrees.value || selectedDegrees.value.has(classroom.degree)
        )
        .map((classroom: ExportFilterSchoolClassroom) => ({
          label: classroom.name,
          value: classroom.id
        }))
    }
  })
})

/**
 * Create the export
 * @param key
 * @param diagnosticType
 */
const createExport = async (key: ExportType.Values, diagnosticType?: QuizType.Values) => {
  try {
    exportsStore.setExportingState(key, diagnosticType ?? null)
    if (!filtersInput.value.isValid()) {
      exportsStore.showErrorToast('Export interrompu', 'Les filtres ne sont pas valides')
    }
    const result = await exportsStore.fetchExport(key, filtersInput.value.copy({ diagnosticType }))
    saveAs(result.blob, result.filename)
  } finally {
    exportsStore.setExportingState(null, null)
  }
}

/**
 * Reset the filters (degrees and classrooms) when the selected school changes
 */
const onUpdateSchools = () => {
  filtersInput.value = filtersInput.value.copy({ degrees: [], classrooms: [] })
}

/**
 * Reset the filters (classrooms) when the selected school changes
 */
const onUpdateDegrees = () => {
  filtersInput.value = filtersInput.value.copy({ classrooms: [] })
}

/**
 * Watcher for the selected school year
 */
watch(
  exportSelectedSchoolYear,
  (newYear) => {
    if (!newYear || filtersInput.value.year !== 0) return
    filtersInput.value = ExportFilterInput.fromYear(newYear.schoolYearId)
  },
  { immediate: true }
)
</script>

<template>
  <c-base-block :toggleable="false" title="Exporter des données">
    <section aria-labelledby="schools-title" class="my-2">
      <h4 id="schools-title" class="mb-2 block flex-auto text-lg font-bold">Lycées</h4>
      <p-multi-select
        :options="schoolsOptions"
        v-model="filtersInput.schools"
        @update:model-value="onUpdateSchools"
        input-id="schools"
        :disabled="existsReportingState"
        option-label="label"
        option-value="value"
        show-clear
        filter
        display="chip"
        aria-labelledby="schools-title"
        placeholder="Sélectionner un ou plusieurs lycées"
        class="w-full md:w-1/3"
      />
    </section>

    <section aria-labelledby="degrees-title" class="my-4">
      <h4 id="degrees-title" class="mb-2 block flex-auto text-lg font-bold">Niveaux</h4>
      <p-multi-select
        :options="degreesOptions"
        v-model="filtersInput.degrees"
        @update:model-value="onUpdateDegrees"
        input-id="degrees"
        :disabled="existsReportingState"
        option-label="label"
        option-value="value"
        show-clear
        filter
        display="chip"
        aria-labelledby="degrees-title"
        placeholder="Sélectionner un ou plusieurs lycées"
        class="w-full md:w-1/3"
      />
    </section>

    <section aria-labelledby="classroom-title" class="my-4">
      <h4 id="classroom-title" class="mb-2 block flex-auto text-lg font-bold">Classes</h4>
      <p-multi-select
        :options="classroomsOptions"
        v-model="filtersInput.classrooms"
        input-id="classrooms"
        :disabled="existsReportingState"
        option-group-label="label"
        option-group-children="items"
        option-label="label"
        option-value="value"
        show-clear
        filter
        display="chip"
        aria-labelledby="classroom-title"
        placeholder="Sélectionner un ou plusieurs lycées"
        class="w-full md:w-1/3"
      />
    </section>

    <section aria-labelledby="exports-title" class="my-4 flex flex-col gap-2">
      <h4 id="exports-title" class="mb-2 block flex-auto text-lg font-bold">Exports</h4>

      <div class="flex flex-col gap-2">
        <label for="global-student-btn"
          >Informations sur l'élève, diagnostics, modules et progression :
        </label>
        <p-button
          label="Exporter les données globales par élève"
          input-id="global-stutent-btn"
          :loading="exportsStore.equalsExportingState(ExportType.Values.GLOBAL_STUDENTS, null)"
          :disabled="existsReportingState"
          outlined
          severity="primary"
          class="w-fit"
          type="button"
          @click.stop.prevent="createExport(ExportType.Values.GLOBAL_STUDENTS, undefined)"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label for="global-student-btn">Diagnostics : </label>
        <div class="flex gap-2">
          <p-button
            label="Exporter les résultats du diagnostic de début d'année"
            input-id="diag-start-result-btn"
            :loading="
              exportsStore.equalsExportingState(
                ExportType.Values.DIAGNOSTIC,
                QuizType.Values.DIAG_START
              )
            "
            :disabled="existsReportingState"
            outlined
            severity="primary"
            class="w-fit"
            type="button"
            @click.stop.prevent="
              createExport(ExportType.Values.DIAGNOSTIC, QuizType.Values.DIAG_START)
            "
          />
          <p-button
            label="Exporter les résultats du diagnostic de fin d'année"
            input-id="diag-end-result-btn"
            :loading="
              exportsStore.equalsExportingState(
                ExportType.Values.DIAGNOSTIC,
                QuizType.Values.DIAG_END
              )
            "
            :disabled="existsReportingState"
            outlined
            severity="primary"
            class="w-fit"
            type="button"
            @click.stop.prevent="
              createExport(ExportType.Values.DIAGNOSTIC, QuizType.Values.DIAG_END)
            "
          />
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <label for="module-result-btn"
          >Modules (les modules peuvent être différents pour les élèves d'établissements différents)
          :
        </label>
        <p-button
          label="Exporter les résultats par modules"
          input-id="module-result-btn"
          :loading="exportsStore.equalsExportingState(ExportType.Values.MODULE_RESULTS, null)"
          :disabled="existsReportingState"
          outlined
          severity="primary"
          class="w-fit"
          type="button"
          @click.stop.prevent="createExport(ExportType.Values.MODULE_RESULTS, undefined)"
        />
      </div>

      <div class="flex flex-col gap-2">
        <label for="activity-btn">Connexions : </label>
        <p-button
          label="Exporter les données de connexions par élève"
          input-id="activity-btn"
          :loading="exportsStore.equalsExportingState(ExportType.Values.LOGIN_HISTORY, null)"
          :disabled="existsReportingState"
          outlined
          severity="primary"
          class="w-fit"
          type="button"
          @click.stop.prevent="createExport(ExportType.Values.LOGIN_HISTORY, undefined)"
        />
      </div>
    </section>
  </c-base-block>
</template>